<template>
   <pb-body-base :busy="loading">
      <template v-slot:default>
         <pb-body banner-size="sm"
                  :banner-img="fileResourceUrl(farbkarte.karte)"
                  :page-title="modalTitle">
            <template slot="body">
               <b-container>
                  <div v-if="error">
                     <b-alert variant="warning" show>{{ error }}</b-alert>
                  </div>
                  <div v-if="farbkarte && farbenWithFoto.length > 0" class="farbkarte-grid" :class="'bg-' + selectedBg">
                     <div class="bg-switcher mb-3">
                        <span class="bg-switcher-label">Background:</span>
                        <button v-for="bg in backgrounds" :key="bg.key"
                                class="bg-swatch"
                                :class="{ active: selectedBg === bg.key }"
                                :style="{ backgroundColor: bg.color }"
                                :title="bg.label"
                                @click="selectedBg = bg.key">
                        </button>
                     </div>
                     <b-row>
                        <div v-for="(color, index) in farbenWithFoto"
                             :key="index" class="col-md-4 col-lg-2dot4 article-link mb-2 card-deck">
                           <div class="color-card"
                                :class="{ 'is-open': openIndex === index, 'has-tags': hasTags(color) }"
                                @click.stop="toggleTags(index, color)">
                              <pb-card :hoverable="false">
                                 <template slot="image">
                                    <img :src="imageResource(color.foto, 240)"
                                         alt="thumbnail"/>
                                 </template>
                                 <template slot="content">
                              <span
                                  class="d-block">{{ color.farbnummer }}</span>
                                 </template>
                              </pb-card>
                              <span v-if="color.certified"
                                    class="icon-oeko-tex color-card-badge"
                                    title="OEKO-TEX certified"></span>
                              <button v-if="hasTags(color)"
                                      type="button"
                                      class="color-card-info"
                                      :aria-expanded="openIndex === index ? 'true' : 'false'"
                                      aria-label="Show material details"
                                      @click.stop="toggleTags(index, color)">
                                 <i class="fal fa-info-circle"></i>
                              </button>
                              <div v-if="hasTags(color)" class="color-card-overlay">
                                 <ul class="color-card-tags">
                                    <li v-for="(tag, tagIndex) in color.tags" :key="tagIndex">{{ tag }}</li>
                                 </ul>
                              </div>
                           </div>
                        </div>
                     </b-row>
                  </div>
                  <b-alert v-else-if="farbkarte && farbenWithFoto.length === 0" variant="info" show>No color images available for this material.</b-alert>
               </b-container>
            </template>
         </pb-body>
      </template>
   </pb-body-base>
</template>

<script>
import ArticleApi from "@shared/_api/article";

export default {
   name: 'farbkarte-detail',
   data() {
      return {
         loading: false,
         farbkarte: null,
         error: null,
         openIndex: null,
         selectedBg: 'light',
         backgrounds: [
            { key: 'light', label: 'Light Gray', color: '#f0f0f0' },
            { key: 'dark-gray', label: 'Dark Gray', color: '#616160' },
            { key: 'dark', label: 'Dark', color: '#333333' }
         ]
      }
   },
   computed: {
      farbenWithFoto() {
         if (this.farbkarte && this.farbkarte.farben) {
            return this.farbkarte.farben.filter(c => c.foto);
         }
         return [];
      },
      modalTitle() {
         if (this.farbkarte) {
            return `Color Card - ${this.farbkarte.qualitaetnummer}`;
         }
         return 'Color Card';
      }
   },
   mounted() {
      this.loadFarbkarte(this.$route.params.qualitaetId);
      document.addEventListener('click', this.closeTags);
   },
   beforeDestroy() {
      document.removeEventListener('click', this.closeTags);
   },
   beforeRouteUpdate(to, from, next) {
      this.loadFarbkarte(to.params.qualitaetId);
      next();
   },
   methods: {
      loadFarbkarte(qualitaetId) {
         this.loading = true;
         this.farbkarte = null;
         this.error = null;
         this.openIndex = null;

         ArticleApi.farbkarteByQualitaetId(qualitaetId)
             .then(data => {
                this.farbkarte = data;
             })
             .catch(() => {
                this.error = 'No color card found for this quality.';
             })
             .finally(() => {
                this.loading = false;
             });
      },
      hasTags(color) {
         return !!(color.tags && color.tags.length > 0);
      },
      // Touch devices get no :hover, and this page is what the QR code on the physical
      // color card opens, so the tag overlay has to be reachable by tap as well.
      toggleTags(index, color) {
         if (!this.hasTags(color)) {
            return;
         }
         this.openIndex = this.openIndex === index ? null : index;
      },
      closeTags() {
         this.openIndex = null;
      },
      onImageError(event, color) {
         let el = event.target;
         let parent = el.parentElement;
         el.style.display = 'none';
         let fallback = document.createElement('div');
         fallback.className = 'farbkarte-color-number';
         fallback.textContent = color.farbnummer;
         parent.insertBefore(fallback, el);
      }
   }
}
</script>

<style lang="scss" scoped>
@import "~@shared/_assets/scss/constants";

.farbkarte-banner {
   max-height: 400px;
   object-fit: contain;
}

.bg-switcher {
   display: flex;
   align-items: center;
   gap: 8px;

   .bg-switcher-label {
      font-size: 13px;
      font-weight: 500;
      color: #666;
   }

   .bg-swatch {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 2px solid #ccc;
      cursor: pointer;
      padding: 0;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:hover {
         border-color: #888;
      }

      &.active {
         border-color: #333;
         box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
      }
   }
}

.color-card {
   position: relative;
   flex: 1 0 0%;
   display: flex;
   flex-direction: column;
   overflow: hidden;
   border-radius: 6px;
   transition: box-shadow 0.25s ease, transform 0.25s ease;

   &.has-tags {
      cursor: pointer;
   }

   &:hover {
      box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.3);
      transform: translateY(-2px);
   }

   // Certification is a trust signal, so it stays on the card face rather than hiding
   // behind an interaction, and sits above the overlay so hovering never conceals it.
   .color-card-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 3;
      line-height: 0;

      // Global .icon-oeko-tex renders at 24px; the card face can carry more.
      &:before {
         width: 32px;
         height: 32px;
      }
   }

   // The only cue on a tagged but uncertified card, so it carries a full 44px hit area
   // even though the glyph itself renders small.
   .color-card-info {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 3;
      width: 44px;
      height: 44px;
      // The 44px target stays, but the glyph is inset to 12px so it lines up with
      // the OEKO-TEX badge on the opposite corner.
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      padding: 12px;
      border: 0;
      background: none;
      cursor: pointer;

      .fal {
         color: $color-primary;
         font-size: 20px;
         transition: color 0.2s ease;
      }

      &:hover .fal,
      &:focus .fal {
         color: $color-primary-dark;
      }
   }

   .color-card-overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 16px;
      text-align: center;
      color: #ffffff;
      background: rgba(17, 17, 17, 0.5);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.25s ease, visibility 0.25s ease;
   }

   // Tap-driven state, the only one touch devices ever reach.
   &.is-open .color-card-overlay {
      opacity: 1;
      visibility: visible;
   }

   .color-card-tags {
      list-style: none;
      margin: 0;
      padding: 0;
      font-family: $font-robo;
      font-size: 13px;
      font-weight: 300;
      line-height: 1.5;
      // The scrim is only 50%, so the text needs its own contrast over pale swatches.
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);

      li + li {
         margin-top: 4px;
      }
   }
}

// Guarded so touch devices, where a tap can leave a sticky :hover, never open the
// overlay outside the explicit is-open state above.
@media (hover: hover) {
   .color-card:hover .color-card-overlay {
      opacity: 1;
      visibility: visible;
   }
}

.farbkarte-grid {
   padding: 20px;
   border-radius: 8px;
   transition: background-color 0.3s ease;

   &.bg-light {
      background-color: #f0f0f0;

      ::v-deep .pb-card .pb-card-img {
         background: #f0f0f0;
      }
   }

   &.bg-dark-gray {
      background-color: #616160;

      ::v-deep .pb-card .pb-card-img {
         background: #616160;
      }

      ::v-deep .pb-card .pb-card-content {
         color: #f0f0f0;
      }

      // The label strip takes the switcher's background, so the info glyph has to
      // follow it or it drops out of contrast.
      .color-card-info .fal {
         color: #f0f0f0;
      }

      .color-card-info:hover .fal,
      .color-card-info:focus .fal {
         color: #ffffff;
      }

      .bg-switcher .bg-switcher-label {
         color: #ccc;
      }
   }

   &.bg-dark {
      background-color: #333333;

      ::v-deep .pb-card .pb-card-img {
         background: #333333;
      }

      ::v-deep .pb-card .pb-card-content {
         color: #f0f0f0;
      }

      .color-card-info .fal {
         color: #f0f0f0;
      }

      .color-card-info:hover .fal,
      .color-card-info:focus .fal {
         color: #ffffff;
      }

      .bg-switcher .bg-switcher-label {
         color: #ccc;
      }
   }

   .farbkarte-color-item {
      border: 1px solid #eee;
      border-radius: 4px;
      min-height: 100px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
   }

   .farbkarte-color-img {
      max-height: 120px;
      object-fit: contain;
   }

   .farbkarte-color-number {
      font-size: 20px;
      font-weight: bold;
      padding: 20px 0;
   }
}
</style>
